import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import MentionBase from './MentionBase';
import * as styles from './PostMention.module.scss';
import type { PillSize } from '../../components/Pill';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated/gql';
import { PostType } from '../../graphql/generated/graphql';
import publicIcon from '../../icons/non-prefilled/16x16/public.svg';
import { getStateIcon } from '../../post/PostIcon';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from '../../post/postPolicyCheck';
import { useAllSlimPostMap } from '../../post/useAllSlimPosts';
import { parseShareSecretFromParams } from '../../post/useShareSecret';
import { UserRole } from '../../topic/topicPolicyCheck';
import postPath, { publicPostPath } from '../../utils/url/postPath';
import useIsPublicView from '../../utils/useIsPublicView';
import { parseMentionId } from '../utils/matchMention';

const POST_QUERY = gql(/** @complexity 1 */ `
  query PostMentionPost($id: ID!) {
    post(id: $id) {
      id
      title
      type
      ...isAllowedThroughLinkAccess_post
    }
  }
`);

const PUBLIC_POST_QUERY = gql(/** @complexity 1 */ `
  query PostMentionPublicPost($id: ID!, $shareSecret: String!) {
    post: publicPost(id: $id, shareSecret: $shareSecret) {
      id
      title
      ...isAllowedThroughLinkAccess_post
    }
  }
`);

interface PostMentionProps {
  id: string;
  content: string | null;
  mentionText?: string | null;
  updateCache?: (name?: string) => void;
  link: boolean;
  hideIcon?: boolean;
  newTab?: boolean;
  params?: string;
  size?: PillSize;
  selected?: boolean;
}

const PostMention = ({
  id: mentionId,
  content,
  mentionText,
  updateCache,
  link,
  hideIcon,
  newTab,
  params,
  size,
  selected,
}: PostMentionProps) => {
  const shareSecret = parseShareSecretFromParams(params);

  const isPublicView = useIsPublicView();
  const { anchor, id: postId } = useMemo(
    () => parseMentionId(mentionId),
    [mentionId],
  );

  const allPostMap = useAllSlimPostMap();
  const postFromAllPostMap = allPostMap[postId];

  // TODO: Not needed after https://github.com/slab/slab/issues/9648
  const { data } = useQuery(POST_QUERY, {
    variables: { id: postId },
    fetchPolicy: 'cache-first',
    skip: !!content || isPublicView || !!postFromAllPostMap,
  });

  const { data: publicData } = useQuery(PUBLIC_POST_QUERY, {
    variables: { id: postId, shareSecret: shareSecret ?? postId },
    skip: !isPublicView,
  });

  const post = postFromAllPostMap ?? data?.post ?? publicData?.post;

  const isPubliclyAccessible = post
    ? isAllowedThroughLinkAccess(
        PostPolicyAction.ViewPost,
        post,
        UserRole.Anonymous,
      )
    : false;

  const path =
    isPublicView && isPubliclyAccessible
      ? publicPostPath(
          { id: postId, title: content ?? '' },
          { anchor, shareSecret },
        )
      : postPath(post ?? { id: postId, title: content ?? '' }, {
          anchor,
          shareSecret,
        });

  useEffect(() => {
    if (!isPublicView && post?.title != null && content == null) {
      updateCache?.(post.title);
    }
  }, [updateCache, isPublicView, post?.title, content]);

  const isTemplate =
    post && 'type' in post ? post.type === PostType.Template : false;

  return (
    <MentionBase
      newTab={newTab}
      to={link ? path : undefined}
      icon={
        hideIcon
          ? undefined
          : getStateIcon(
              {
                type: isTemplate ? PostType.Template : PostType.Post,
                draft: false,
                archived: false,
              },
              !isTemplate,
            )
      }
      type={isTemplate ? 'template' : 'post'}
      content={mentionText ?? post?.title ?? content ?? undefined}
      iconAfter={
        isPubliclyAccessible && (
          <SVGIcon inline className={styles.publicIcon} size={14}>
            {publicIcon}
          </SVGIcon>
        )
      }
      selected={selected}
      size={size}
    />
  );
};

export default PostMention;
